import { useMemo } from 'react';
import type { AIAnalysisArtifact } from '../types';

/**
 * Processed artifact with RegExp pattern for UI matching
 */
export interface ProcessedArtifact extends AIAnalysisArtifact {
  pattern: RegExp;
}

/**
 * Hook to convert backend artifact patterns (strings) to RegExp objects for UI matching
 *
 * @param artifacts - Raw artifacts from backend with pattern as string or RegExp
 * @returns Processed artifacts with RegExp patterns, filtering out any invalid patterns
 *
 * @example
 * ```tsx
 * const { artifacts } = useEntityAgenticTriageReportContext();
 * const processedArtifacts = useProcessArtifacts(artifacts);
 *
 * // Now artifacts have RegExp patterns ready for matching
 * processedArtifacts.forEach(artifact => {
 *   const matches = text.match(artifact.pattern);
 * });
 * ```
 */
export function useProcessArtifacts(
  artifacts: AIAnalysisArtifact[] | undefined,
): ProcessedArtifact[] {
  return useMemo(() => {
    if (!artifacts || artifacts.length === 0) return [];

    return artifacts
      .map((artifact) => {
        // Convert pattern to RegExp if it's a string
        let pattern: RegExp | undefined;

        if (typeof artifact.pattern === 'string') {
          try {
            pattern = new RegExp(artifact.pattern, 'g');
          } catch (e) {
            console.warn(
              `Invalid regex pattern for artifact ${artifact.id}:`,
              e,
            );
            return null;
          }
        } else if (artifact.pattern instanceof RegExp) {
          // Ensure the RegExp has the global flag for matching multiple occurrences
          pattern = artifact.pattern.global
            ? artifact.pattern
            : new RegExp(artifact.pattern.source, 'g');
        }

        if (!pattern) return null;

        return {
          ...artifact,
          pattern,
        };
      })
      .filter((artifact): artifact is ProcessedArtifact => artifact !== null);
  }, [artifacts]);
}
